<template>
  <div>
    <v-system-bar app dark color="accent" height="33" class="system-bar">
      <small class="powered-by ml-3">Powered by Christian Aid with support from Start Network</small>
      <v-spacer></v-spacer>
      <div class="d-flex pr-3 mr-3">
        <div @click.prevent="$emit('contact')" class="link ml-4">
          <v-icon class="mr-1">help_outline</v-icon>
          <small class="link-text">Contact</small>
        </div>
        <router-link v-for="item in systemBarItems" :key="item.title" :to="item.link" class="link ml-4">
          <v-icon class="mr-1">{{ item.icon }}</v-icon>
          <small class="link-text">{{ item.title }}</small>
        </router-link>
        <div v-if="isAuthenticated" @click.prevent="$emit('logout')" class="link ml-4">
          <v-icon class="mr-1">power_settings_new</v-icon>
          <small class="link-text">Logout</small>
        </div>
      </div>
    </v-system-bar>

    <v-toolbar app fixed clipped-left dark color="secondary">
      <v-toolbar-side-icon
        @click.native="navSideOpen = !navSideOpen"
        v-if="isAuthenticated && showDrawer && showDrawerIcons"
      ></v-toolbar-side-icon>
      <v-toolbar-title class="headline text-uppercase">
        <router-link to="/">
          <span class="white--text">HUIREP</span>
        </router-link>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn flat v-for="item in topNavItems" :key="item.title" :to="item.link">{{ item.title }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-navigation-drawer
      app
      floating
      clipped
      height="100vh"
      width="245px"
      mobile-break-point="768px"
      v-model="navSideOpen"
      v-if="isAuthenticated && showDrawer"
    >
      <v-toolbar flat class="mt-2 py-4">
        <v-list class="pa-0">
          <v-list-tile avatar>
            <v-list-tile-avatar>
              <img src="@/assets/images/dummy-avatar.png" />
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title>
                <b>{{ currentUser.name }}</b>
              </v-list-tile-title>
              <v-list-tile-sub-title>
                {{ $lang(currentUser.role) }}
                <span
                  class="d-block grey--text"
                  v-if="currentUser.parent === 'state'"
                >{{ stateName(currentUser.parent_id) }}</span>
                <span
                  class="d-block grey--text"
                  v-if="currentUser.parent === 'agency'"
                >{{ agencyName(currentUser.parent_id) }}</span>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-toolbar>

      <v-divider></v-divider>

      <v-list>
        <v-list-tile
          v-for="item in singleNavItems"
          :key="item.title"
          :to="item.link"
          active-class="accent--text"
        >
          <v-list-tile-action v-if="showDrawerIcons">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-tile-action>
          <v-list-tile-title>{{ item.title }}</v-list-tile-title>
        </v-list-tile>

        <v-list-group :value="false" no-action v-if="$can('view_analytics')">
          <template v-slot:activator>
            <v-list-tile>
              <v-list-tile-action v-if="showDrawerIcons">
                <v-icon>assessment</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Analytics</v-list-tile-title>
            </v-list-tile>
          </template>
          <v-list-tile
            v-for="item in analyticsNavItems"
            :key="item.title"
            :to="item.link"
            active-class="accent--text"
          >
            <v-list-tile-title>{{ item.title }}</v-list-tile-title>
          </v-list-tile>
        </v-list-group>

        <v-list-group :value="false" no-action v-if="$can('view_users')">
          <template v-slot:activator>
            <v-list-tile>
              <v-list-tile-action v-if="showDrawerIcons">
                <v-icon>account_circle</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Users</v-list-tile-title>
            </v-list-tile>
          </template>
          <v-list-tile
            v-for="item in userNavItems"
            :key="item.title"
            :to="item.link"
            active-class="accent--text"
          >
            <v-list-tile-title>{{ item.title }}</v-list-tile-title>
          </v-list-tile>
        </v-list-group>
      </v-list>
      <v-spacer></v-spacer>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { singleNavItems, analyticsNavItems, userNavItems } from '@/data/sideNavItems' 
import { STATE_FETCH } from '@/store/actions/state'
import { AGENCY_FETCH } from '@/store/actions/agency'

export default {
  name: 'Nav',
  props: ['isAuthenticated', 'currentUser'],
  created () {
    if (this.isAuthenticated) {
      this.$store.dispatch(STATE_FETCH)
      this.$store.dispatch(AGENCY_FETCH)
    }
    document.onreadystatechange = () => {
      if (document.readyState === 'complete') {
        this.showDrawerIcons = true
      }
    }
  },
  data () {
    return {
      navSideOpen: true,
      showDrawerIcons: false
    }
  },
  computed: {
    showDrawer () {
      const excludedRoutes = ['home', 'analytics', 'auth.login', 'auth.logout']
      if (excludedRoutes.includes(this.$route.name)) {
        return false
      }
      return true
    },
    systemBarItems () {
      let items = []

      if (!this.isAuthenticated) {
        items = [
          { icon: 'account_circle', title: 'Log In', link: '/auth/login' }
        ]
      }

      return items
    },
    topNavItems () {
      let items = [
        { icon: 'lock_open', title: 'Home', link: '/' },
        { icon: 'lock_open', title: 'Analytics', link: '/analytics' }
      ]

      if (this.isAuthenticated) {
        items = [
          ...items,
          { icon: 'dashboard', title: 'Dashboard', link: '/dashboard' },
          { icon: 'account_circle', title: 'Edit Profile', link: '/profile' }
        ]
      }

      return items
    },
    singleNavItems () {
      let items = singleNavItems.filter(item => {
        return this.$can(item.permission)
      })
      return items
    },
    userNavItems () {
      let items = userNavItems.filter(item => {
        return this.$can(item.permission)
      })
      return items
    },
    analyticsNavItems () {
      let items = analyticsNavItems.filter(item => {
        return this.$can(item.permission)
      })
      return items
    }
  },
  methods: {
    stateName (id) {
      return this.$store.getters.stateName(id)
    },
    agencyName (id) {
      return this.$store.getters.agencyName(id)
    }
  }
}
</script>

<style scoped>
.headline {
  font-family: system-ui, Arial, Helvetica, sans-serif !important;
  font-weight: 700
}

.system-bar a,
.system-bar .link-text {
  color: #ffffff;
  text-transform: uppercase;
  cursor: pointer
}

.system-bar .link {
  cursor: pointer
}

@media screen and (max-width: 468px) {
  .system-bar .powered-by {
    display: none;
    height: 0px;
  }
}

.v-list__tile__sub-title {
  line-height: 20px 
}
</style>
